import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import Sparkline from "@/components/Sparkline";

const slug = (name) => name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

export const TrendsGrid = ({ trends, showAllTrends, setShowAllTrends }) => {
  if (!trends.markers || trends.markers.length === 0) return null;

  const isSnapshot = trends.mode === "snapshot";
  const notable = trends.markers.filter(m => ["attention", "borderline"].includes((m.zone || {}).status));
  const shouldCollapse = trends.markers.length > 12 && notable.length > 0;
  const visibleMarkers = shouldCollapse && !showAllTrends ? notable : trends.markers;

  return (
    <div className="mt-8" data-testid="trends-section">
      <p className="label-eyebrow mb-1" data-testid="trends-mode">
        {trends.mode === "calendar" && "30-day trends · vs prior 30 days"}
        {trends.mode === "visit" && `Visit comparison · ${trends.latest_visit_date} vs ${trends.prior_visit_date}`}
        {trends.mode === "snapshot" && `Latest values · ${trends.latest_visit_date}`}
      </p>
      {isSnapshot && (
        <p className="text-xs text-[#5C6359] mb-4">
          {shouldCollapse && !showAllTrends
            ? `Showing ${notable.length} marker${notable.length === 1 ? "" : "s"} that need attention. Upload a second lab to see trends with ↑/↓ arrows.`
            : "Upload a second lab to see trends with ↑/↓ arrows."}
        </p>
      )}
      {!isSnapshot && shouldCollapse && !showAllTrends && (
        <p className="text-xs text-[#5C6359] mb-4">
          Showing {notable.length} marker{notable.length === 1 ? "" : "s"} that need attention.
        </p>
      )}
      {!isSnapshot && !shouldCollapse && <div className="mb-4" />}
      {!isSnapshot && shouldCollapse && showAllTrends && <div className="mb-4" />}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {visibleMarkers.map((t) => {
          const isUp = t.direction === "up";
          const isDown = t.direction === "down";
          const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
          const tone = isUp ? "text-[#C05C42]" : isDown ? "text-[#2A3B2C]" : "text-[#5C6359]";
          const z = t.zone;
          const statusColor = z?.status === "optimal"
            ? { bg: "bg-[#2A3B2C]/10", text: "text-[#2A3B2C]", border: "border-[#2A3B2C]/30" }
            : z?.status === "attention"
              ? { bg: "bg-[#8F3A3A]/10", text: "text-[#8F3A3A]", border: "border-[#8F3A3A]/30" }
              : z?.status === "borderline"
                ? { bg: "bg-[#D49A44]/15", text: "text-[#8a6620]", border: "border-[#D49A44]/40" }
                : null;
          return (
            <div key={t.marker} className={`bg-[#F1ECE4] border ${statusColor ? statusColor.border : "border-[#D1C7B8]"} rounded-2xl p-4 min-w-0`} data-testid={`trend-${slug(t.marker)}`}>
              <div className="flex flex-col gap-2">
                {z && (
                  <span data-testid={`zone-${slug(t.marker)}`} className={`self-start text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full ${statusColor.bg} ${statusColor.text}`}>
                    {z.zone}
                  </span>
                )}
                <p className="text-[10px] uppercase tracking-widest text-[#5C6359] leading-snug break-words">{t.marker}</p>
              </div>
              <p className="font-serif text-2xl text-[#2D322B] mt-2">
                {t.current_avg ?? "—"} <span className="text-xs text-[#5C6359]">{t.unit}</span>
              </p>
              {z && (
                <p className="text-[10px] text-[#5C6359] mt-0.5">
                  Optimal · {z.optimal_low}–{z.optimal_high} {z.unit}
                </p>
              )}
              {t.series && t.series.length > 1 && (
                <div className="mt-3 -mx-1" data-testid={`sparkline-${slug(t.marker)}`}>
                  <Sparkline
                    series={t.series}
                    optimalLow={z?.optimal_low}
                    optimalHigh={z?.optimal_high}
                    status={z?.status}
                    width={220}
                    height={42}
                  />
                </div>
              )}
              {!isSnapshot && t.delta_pct !== null && (
                <div className={`mt-2 flex items-center gap-1 text-xs ${tone}`}>
                  <Icon className="w-3.5 h-3.5" />
                  <span>{t.delta_pct > 0 ? "+" : ""}{t.delta_pct}% vs prior</span>
                </div>
              )}
              {!isSnapshot && t.count_previous > 0 && (
                <p className="mt-1 text-[10px] text-[#5C6359]">{t.count_current} now · {t.count_previous} prior</p>
              )}
            </div>
          );
        })}
      </div>
      {shouldCollapse && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setShowAllTrends(v => !v)}
            data-testid="toggle-show-all-trends"
            className="text-xs uppercase tracking-widest text-[#C05C42] hover:text-[#2D322B] inline-flex items-center gap-1.5"
          >
            {showAllTrends ? `Show only ${notable.length} attention markers` : `Show all ${trends.markers.length} markers`}
          </button>
        </div>
      )}
    </div>
  );
};
